// Карточка сделки — центральный экран CRM: с него работают каждый день.
//
// ВИД — КАК В BITRIX, НАРОЧНО. Люди годами работали в карточке Bitrix, и на переходный
// период карточка CRM повторяет её раскладку: шапка с названием и суммой, полоса стадий
// воронки, вкладки, слева поля секциями, справа лента событий. Каркас собран из общих
// компонентов (components/card: CardShell, StageBar, FieldSection/FieldRow, Timeline) —
// во второй волне из них же собираются карточки компаний, контактов и судов.
//
// ОДИН ЗАПРОС, А НЕ ВОСЕМЬ. Всё сквозное берётся из ОДНОЙ ручки ядра `/chain/deal?id=`
// (chain_api.py): она уже склеивает шесть смарт-процессов, две таблицы портала 73,
// зеркало документов 1С и состав — одним SQL. Ходить в каждый источник по отдельности
// значило бы не только восемь запросов вместо одного, но и восемь РАЗНЫХ правд: «оплачен
// ли счёт» и «есть ли разрыв» считались бы здесь, а в разделе «Путь сделки» — в ядре, и
// однажды они разошлись бы. Своих правил про данные в этом файле нет ни одного.
//
// ЧТО ГДЕ ЛЕЖИТ. «Общие» — поля 14 разделами ТОЧНО ПО РАСКЛАДКЕ BITRIX (api/dealLayout.ts:
// порядок снят из DOM боевой карточки, подписи и типы — из зеркала bx_fields), поверх них
// поля конструктора и наши поля deal_ext; «Товары и услуги» — состав с СВЕРКОЙ против суммы;
// «Счета» — дебиторка по сделке и журнал переносов срока; «Документы» — документы 1С;
// «Путь сделки» — цепочка по этапам и то, что рядом по судну. Вывод «где встало» стоит
// НАД вкладками: это ответ на вопрос «что со сделкой сейчас», и прятать его за вкладку
// значило бы вернуть человека к складыванию ответа из шести экранов.
//
// ЗАПИСЬ. Смена стадии (клик по полосе), правка полей, комментарии и лента идут через
// api/cardLive.ts — единственную точку стыковки с ручками ядра (/api/cards/*,
// /api/cards-write/*): запись — в СВОЮ схему, в Bitrix с карточки не пишется ничего.
// Ленте и записи нужен id своей схемы — его приносит сам ответ /own/deal блоком _OWN;
// когда его нет (источник — зеркало), экран говорит об этом словами, а не притворяется,
// что сохранил. Обязательность на смене стадии (причина проигрыша) — отдельным полем
// ввода под полосой, конфликт ревизий — словами «успел такой-то, обновите».
//
// ПУСТОЙ БЛОК НЕ ПОКАЗЫВАЕМ, НО МОЛЧАНИЕ ОБЪЯСНЯЕМ. «Платежей не было» и «мы про платежи
// не знаем» — разные ответы. Ядро различает их состоянием звена ('есть' / 'нет' /
// 'не_знаем' / 'не_сведено'), и карточка нигде не склеивает их в пустую клетку.
//
// Дебиторка здесь — тот же смарт-процесс 134 и тот же расчёт, что в разделе «Дебиторка» и
// в карточке компании. Ни одной своей формулы: долг, просрочка и «оплачен ли платёж»
// считаются в lib/payments.ts (`paymentView` + `summarize`), таблица — общий PaymentsTable.
// Иначе по одной и той же сделке карточка и раздел показали бы разные суммы, и вопрос
// «какой цифре верить» стал бы неразрешимым.

import { Fragment, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import * as cardLive from '../api/cardLive'
import { useПутьСделки } from '../api/chain'
import { DEAL_LAYOUT } from '../api/dealLayout'
import type { LayoutField } from '../api/dealLayout'
import { useDeal, useDealPayments, useFields, useRefs, useRightsApi } from '../api/hooks'
import { useСоставСделки } from '../api/products'
import type { Deal } from '../api/types'
import { CardShell } from '../components/card/CardShell'
import type { CardTab } from '../components/card/CardShell'
import { FieldRow } from '../components/card/FieldRow'
import type { CardField } from '../components/card/FieldRow'
import { FieldSection } from '../components/card/FieldSection'
import { StageBar } from '../components/card/StageBar'
import type { Stage } from '../components/card/StageBar'
import { Timeline } from '../components/card/Timeline'
import type { TimelineEvent } from '../components/card/Timeline'
import { EntityLink } from '../components/EntityLink'
import { PaymentsTable } from '../components/PaymentsTable'
import { QueryState } from '../components/QueryState'
import {
  итогПути, классВажности, состояние, сутьЗаписи, переносыЗадним, плюрал,
} from '../lib/chain'
import type { Звено, ЗаписьЗвена, ПутьСделки, Разрыв } from '../lib/chain'
import { plural } from '../lib/dashboard'
import { fmtDate, fmtMoney, fmtText, pluralN } from '../lib/format'
import { PAY_ENTITY, paymentView, summarize } from '../lib/payments'
import {
  количество, короткоОРасхождении, ндс, номенклатура, разборРасхождения, скидка, типСтроки,
  чтоДелать,
} from '../lib/products'
import { refId, vesselRefId } from '../lib/smart'

/** Страница ядра. Всё, что «по загруженной странице», ограничено этим числом. */
const PAGE = 50

type Вкладка = 'main' | 'products' | 'invoices' | 'docs' | 'path'

/** Подписи правимых полей — для события в ленте («Изменено поле …»). */
const ИМЕНА_ПОЛЕЙ: Record<string, string> = {
  TITLE: 'Название',
  OPPORTUNITY: 'Сумма',
  STAGE_ID: 'Стадия',
  ASSIGNED_BY_ID: 'Ответственный',
}

export default function DealCard() {
  const { id } = useParams<{ id: string }>()
  const q = useDeal(id)
  const refs = useRefs()
  const qc = useQueryClient()
  // Сквозной путь сделки. Запрос уходит сразу и параллельно остальным — он зависит только
  // от id в адресе, а не от загруженной сделки: гнать его вторым кругом значило бы добавить
  // карточке целый этаж ожидания ради данных, которые можно спросить сразу.
  const путь = useПутьСделки(id)
  const [вкладка, setВкладка] = useState<Вкладка>('main')
  // Право записи из /api/me. Это ПОДСКАЗКА, а не защита: сервер проверяет запись сам
  // (rights.проверить_запись). Но кнопка, ведущая к гарантированному отказу, — это
  // обещание, которого интерфейс выполнить не может, и человек честно считает её рабочей.
  const rights = useRightsApi()

  const deal = q.data
  // Id СВОЕЙ схемы — из блока _OWN ответа /own/deal (проба на боевом: bitrix 100322 →
  // _OWN.id 68768). Нового запроса ради него нет: он едет в том же ответе, что и сделка.
  // У зеркала (/api/bx) блока нет — тогда лента и запись честно говорят «не подключено».
  const ownId = deal?._OWN?.id ?? null
  // STAGE_ID здесь — полный код Bitrix («C4:NEW»): берём его прямо из справочника,
  // чтобы не собирать префикс воронки руками.
  const stageOptions = refs.stageOptions(String(deal?.CATEGORY_ID ?? ''))

  // Платежи именно этой сделки. Отбор и порядок — в useDealPayments (там же объяснено,
  // почему по parentId2), расчёт — в lib/payments.ts.
  const pay = useDealPayments(id)
  const payRows = useMemo(
    () => (pay.data?.items ?? []).map((p) => paymentView(p, refs)),
    [pay.data, refs],
  )
  const payTotals = useMemo(() => summarize(payRows), [payRows])

  // Состав сделки — товарные строки Bitrix. Итог по строкам и разницу с суммой сделки
  // считает ЯДРО (products_api.py), здесь только показ: две реализации одной денежной
  // формулы разошлись бы, и вопрос «какой цифре верить» стал бы неразрешимым.
  const состав = useСоставСделки(id)
  const расх = useMemo(() => разборРасхождения(состав.data), [состав.data])

  // Прогрев справочника полей дебиторки. Его читает PaymentsTable (вкладка «Счета»),
  // и без прогрева запрос уходил бы при ПЕРВОМ клике по вкладке — а переключение вкладок
  // не должно стоить ни одного обращения (см. nplus1.test.tsx). Здесь он уходит сразу,
  // параллельно остальным, и кэш общий.
  useFields(PAY_ENTITY)

  // Вывод «где встало» считает ядро (stuck_at + gaps), здесь только слова — тот же
  // `итогПути`, что и на экране «Путь сделки»: два разбора одного ответа однажды разошлись
  // бы, и карточка стала бы спорить с разделом о собственной сделке.
  const п = путь.data
  const итог = useMemo(() => итогПути(п), [п])

  // Лента ядра — настоящая (GET /api/cards/feed, слитая: свои события card_feed +
  // история Bitrix). Ей нужен id своей схемы, поэтому запрос уходит после сделки;
  // без ownId cardLive отвечает «не подключено» БЕЗ похода в сеть — счётчики nplus1
  // от такой карточки не растут.
  const лента = useQuery({
    queryKey: ['deal-feed', id, ownId],
    queryFn: () => cardLive.лентаКарточки('deal', ownId),
    enabled: !!id && deal !== undefined,
    staleTime: Infinity,
  })

  // Свои поля из конструктора владельца (GET /api/cards/fields): определения + значения
  // этой записи. Грузятся сразу с картой, а не по клику вкладки: переключение вкладок
  // не должно стоить ни одного обращения (nplus1.test.tsx).
  const своиПоля = useQuery({
    queryKey: ['deal-card-fields', id, ownId],
    queryFn: () => cardLive.поляКарточки('deal', ownId),
    enabled: !!id && deal !== undefined,
    staleTime: Infinity,
  })

  // Сотрудники своей схемы — выпадашка «Ответственный». Грузятся только когда у
  // карточки есть своя запись (без ownId правки нет и список не нужен); один общий
  // ключ на все карточки — состав меняется редко.
  const сотрудникиСвоей = useQuery({
    queryKey: ['own-employees'],
    queryFn: cardLive.сотрудникиДляВыбора,
    enabled: !!ownId,
    staleTime: Infinity,
  })

  // Догруженные страницы ленты («Показать ещё»): offset-страницы поверх первой.
  const [догруз, setДогруз] = useState<{ items: cardLive.СобытиеЛенты[]
    next: number | null } | null>(null)
  const [ещёГрузим, setЕщёГрузим] = useState(false)

  // События, случившиеся В ЭТОЙ сессии (наши правки и комментарии): ядро пишет их в
  // card_feed само, но лента не перечитывается — то, что человек сделал только что,
  // дописывается в список без нового запроса.
  const [сессионные, setСессионные] = useState<TimelineEvent[]>([])
  const добавитьСобытие = (с: Omit<TimelineEvent, 'id' | 'at'>) =>
    setСессионные((были) => [
      ...были,
      { ...с, id: `s-${были.length}`, at: new Date().toISOString() },
    ])

  // Смена сделки в адресе — чужие сессионные события и догруз ленты ей не принадлежат.
  useEffect(() => {
    setСессионные([])
    setДогруз(null)
  }, [id])

  const [стадияЖдёт, setСтадияЖдёт] = useState(false)
  const [стадияОшибка, setСтадияОшибка] = useState('')
  // Отказ обязательности на смене стадии: ядро назвало недостающее поле (обычно
  // lost_reason на проигрышной), экран предлагает ввод причины и повторяет с values.
  const [причинаДля, setПричинаДля] = useState<{ стадия: Stage; код: string
    почему: string } | null>(null)
  const [причина, setПричина] = useState('')

  const обновитьСделку = async () => {
    await qc.invalidateQueries({ queryKey: ['deal', id] })
    void qc.invalidateQueries({ queryKey: ['deals'] })
  }

  // Отправляем только одно реально изменённое поле (FieldRow неизменённое не шлёт).
  // TITLE и OPPORTUNITY уходят в свою схему (/cards-write/update, коды title/amount);
  // STAGE_ID — своей ручкой stage-set: у него своё событие ленты и обязательность.
  const сохранитьПоле = async (код: string, значение: unknown) => {
    if (код === 'STAGE_ID') {
      const итог = await cardLive.сменитьСтадию(ownId, String(значение ?? ''))
      if (!итог.ok) throw new Error(итог.почему)
      добавитьСобытие({
        kind: 'stage',
        text: `Стадия: ${refs.stageName(deal?.STAGE_ID)}`
          + ` → ${refs.stageName(String(значение ?? ''))}`,
        origin: 'crm',
      })
    } else {
      await cardLive.сохранитьПоля(ownId, { [код]: значение })
      добавитьСобытие({
        kind: 'field',
        text: `Изменено поле «${ИМЕНА_ПОЛЕЙ[код] ?? код}»`,
        origin: 'crm',
      })
    }
    await обновитьСделку()
  }

  const сохранитьНашеПоле = async (код: string, значение: unknown) => {
    await cardLive.сохранитьНашеПоле(id!, код, значение)
    добавитьСобытие({ kind: 'field', text: `Изменено наше поле «${код}»`, origin: 'crm' })
    await обновитьСделку()
  }

  // Значение поля из конструктора — POST /cards-write/field-value-save. Событие 'field'
  // в card_feed ядро пишет само (той же транзакцией), здесь оно дописывается сессионно.
  const сохранитьСвоёПоле = async (код: string, значение: unknown) => {
    await cardLive.сохранитьЗначениеПоля(ownId, код, значение)
    const имя = своиПоля.data?.custom.find((п) => п.code === код)?.label ?? код
    добавитьСобытие({ kind: 'field', text: `Изменено наше поле «${имя}»`, origin: 'crm' })
    await qc.invalidateQueries({ queryKey: ['deal-card-fields', id] })
  }

  const перейтиНаСтадию = async (с: Stage, values?: Record<string, unknown>) => {
    if (!deal || (с.id === String(deal.STAGE_ID ?? '') && !values)) return
    setСтадияЖдёт(true)
    setСтадияОшибка('')
    try {
      const итог = await cardLive.сменитьСтадию(ownId, с.id, values)
      if (итог.ok) {
        setПричинаДля(null)
        setПричина('')
        добавитьСобытие({
          kind: 'stage',
          text: `Стадия: ${итог.стадия?.from_name ?? refs.stageName(deal.STAGE_ID)}`
            + ` → ${итог.стадия?.to_name ?? с.name}`,
          origin: 'crm',
        })
        await обновитьСделку()
      } else if (итог.вид === 'required' && итог.поля) {
        const код = Object.keys(итог.поля)[0]
        setПричинаДля({ стадия: с, код, почему: итог.поля[код] || итог.почему })
      } else {
        setСтадияОшибка(итог.почему)
      }
    } catch (e) {
      setСтадияОшибка(e instanceof Error ? e.message : String(e))
    } finally {
      setСтадияЖдёт(false)
    }
  }

  const отправитьКомментарий = async (текст: string) => {
    const р = await cardLive.добавитьКомментарий('deal', ownId, текст)
    if (р.ok) {
      // Дописываем СОХРАНЁННОЕ событие из ответа ядра (с его id и временем), без
      // перечитывания ленты; если ядро события не вернуло — хотя бы сам текст.
      if (р.событие) setСессионные((были) => [...были, р.событие as TimelineEvent])
      else добавитьСобытие({ kind: 'comment', text: текст, origin: 'crm' })
    }
    return р
  }

  const дальшеЛенты = догруз ? догруз.next : (лента.data?.next ?? null)
  const ещёЛенты = async () => {
    if (дальшеЛенты === null || ещёГрузим) return
    setЕщёГрузим(true)
    try {
      const о = await cardLive.лентаКарточки('deal', ownId, дальшеЛенты)
      setДогруз((д) => ({ items: [...(д?.items ?? []), ...о.items], next: о.next }))
    } finally {
      setЕщёГрузим(false)
    }
  }

  // Лента: ответ ядра + догруженные страницы + события этой сессии + то, что знает
  // сама сделка. Порядок наводит Timeline (свежее первым, по дате).
  const события = useMemo<TimelineEvent[]>(() => {
    const из: TimelineEvent[] = [...сессионные]
    for (const с of лента.data?.items ?? []) из.push(с)
    for (const с of догруз?.items ?? []) из.push(с)
    if (deal?.DATE_CREATE) {
      из.push({
        id: 'created', at: String(deal.DATE_CREATE), kind: 'bitrix',
        text: 'Сделка создана', origin: 'bitrix',
      })
    }
    return из
  }, [сессионные, лента.data, догруз, deal])

  // ─── стадии для полосы: справочник ядра + семантика, ничего руками ───
  const стадии = useMemo<Stage[]>(
    () => stageOptions.map((o) => ({ id: o.id, name: o.name, kind: refs.stageKind(o.id) })),
    [stageOptions, refs],
  )

  const естьШагов = п ? п.steps.filter((s) => s.state === 'есть').length : 0
  const вкладки: CardTab[] = [
    { key: 'main', label: 'Общие' },
    { key: 'products', label: 'Товары и услуги' },
    { key: 'invoices', label: pay.data ? `Счета · ${payRows.length}` : 'Счета' },
    // Вкладки пути и документов появляются только когда ядро отдало /chain/deal:
    // вкладка, которая открывает пустоту, обещает данные, которых нет.
    ...(п ? [
      { key: 'docs', label: 'Документы' },
      { key: 'path', label: `Путь сделки · ${естьШагов} из ${п.steps.length}` },
    ] : []),
  ]

  const можноПравить = rights.canWrite
  const onSave = можноПравить ? сохранитьПоле : undefined

  return (
    <>
      <div className="page-head">
        <h1>Сделка #{id}</h1>
        <div className="spacer" />
        {/* Развёрнутый путь остаётся отдельным экраном: там он показан поперёк сущностей
            и рядом со списком «где рвётся чаще». Здесь — тот же ответ, но про эту сделку. */}
        <Link to={`/chain/${id}`}>путь сделки →</Link>
        {' · '}
        <Link to="/deals">← к списку</Link>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error}>
        {deal && (
          <>
            {/* ─── Где сейчас сделка ─── */}
            {/* Первое, что человек читает, и оно НАД вкладками. Всё остальное на этом
                экране отвечает на вопрос «почему», а эта строка — на вопрос «что». */}
            <Вывод путь={путь} п={п} итог={итог} />

            <CardShell
              head={
                <div className="cshell-head">
                  <div className="cshell-title">{fmtText(deal.TITLE)}</div>
                  <div className="spacer" />
                  <div className="cshell-amount">
                    {fmtMoney(deal.OPPORTUNITY, deal.CURRENCY_ID || 'RUB')}
                  </div>
                </div>
              }
              stageBar={
                <>
                  <StageBar
                    stages={стадии}
                    current={String(deal.STAGE_ID ?? '')}
                    onStageClick={можноПравить ? (с) => void перейтиНаСтадию(с) : undefined}
                    busy={стадияЖдёт}
                  />
                  {стадияОшибка && <div className="error">{стадияОшибка}</div>}
                  {/* Отказ обязательности — не тупик: ядро назвало недостающее поле,
                      человек вводит причину здесь же и переход повторяется с values. */}
                  {причинаДля && (
                    <div className="notice" style={{ marginTop: 8 }}>
                      Для стадии «{причинаДля.стадия.name}» нужна причина:{' '}
                      {причинаДля.почему}
                      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                        <input
                          value={причина}
                          aria-label="Причина"
                          placeholder="Причина"
                          onChange={(e) => setПричина(e.target.value)}
                        />
                        <button type="button" className="primary"
                                disabled={стадияЖдёт || !причина.trim()}
                                onClick={() => void перейтиНаСтадию(
                                  причинаДля.стадия, { [причинаДля.код]: причина.trim() })}>
                          Провести с причиной
                        </button>
                        <button type="button" disabled={стадияЖдёт}
                                onClick={() => { setПричинаДля(null); setПричина('') }}>
                          Отмена
                        </button>
                      </div>
                    </div>
                  )}
                </>
              }
              tabs={вкладки}
              active={вкладка}
              onTab={(k) => setВкладка(k as Вкладка)}
              left={
                <>
                  {вкладка === 'main' && (
                    <Поля deal={deal} refs={refs} rights={rights}
                          свои={своиПоля.data}
                          сотрудники={сотрудникиСвоей.data}
                          onSave={onSave}
                          onSaveExt={можноПравить ? сохранитьНашеПоле : undefined}
                          onSaveCustom={можноПравить ? сохранитьСвоёПоле : undefined} />
                  )}
                  {вкладка === 'products' && <Состав состав={состав} расх={расх} />}
                  {вкладка === 'invoices' && (
                    <>
                      <Дебиторка pay={pay} payRows={payRows} payTotals={payTotals} />
                      {/* Журнал переносов срока стоит рядом с дебиторкой, и это не
                          вкусовщина: перенос задним числом прячет ровно ту просрочку,
                          которую показывает таблица выше. В Bitrix остаётся только
                          последнее значение срока — истории там нет вовсе. */}
                      {п && п.due_moves.length > 0 && <Переносы п={п} />}
                    </>
                  )}
                  {вкладка === 'docs' && п && <Документы1С п={п} />}
                  {вкладка === 'path' && п && (
                    <>
                      <Цепочка п={п} refs={refs} />
                      {/* «Рядом» есть, пока ядру есть что про него сказать: даже без единой
                          записи там стоит объяснение, почему акты к сделке привязать нельзя.
                          А вот когда список звеньев «рядом» пуст совсем — блока нет. */}
                      {(п.nearby.length > 0 || п.p73_requests.length > 0) && <Рядом п={п} />}
                    </>
                  )}
                </>
              }
              right={
                <>
                  <h2>Лента</h2>
                  <Timeline
                    events={события}
                    onComment={можноПравить ? отправитьКомментарий : undefined}
                    note={лента.data && !лента.data.подключено ? лента.data.почему : undefined}
                    more={дальшеЛенты !== null
                      ? { has: true, loading: ещёГрузим, onMore: () => void ещёЛенты() }
                      : undefined}
                    emptyText="Событий в ленте нет"
                  />
                </>
              }
            />
          </>
        )}
      </QueryState>
    </>
  )
}

// ═══════════════════════════ вкладка «Общие»: поля секциями ═══════════════════════════

/** Пустое поле — серым прочерком, как в Bitrix. Один узел на все строки. */
const ПРОЧЕРК = <span className="frow-empty">—</span>

/**
 * «Значения нет» по-битриксовски. Кроме обычной пустоты (null/''/пустой массив) Bitrix
 * пишет «нет связи» НУЛЁМ у полей-привязок ('0', а не пусто — на этом уже ловилась ссылка
 * «#0»), а у пустых файловых и множественных полей присылает false.
 */
function пустоBitrix(f: LayoutField, v: unknown): boolean {
  if (v === null || v === undefined || v === '') return true
  if (Array.isArray(v) && v.length === 0) return true
  if (v === false && f.type !== 'boolean') return true
  if ((f.type === 'crm' || f.type === 'employee' || f.type === 'user')
      && (v === '0' || v === 0)) return true
  return false
}

/** Подпись пункта списка по id из enum_values зеркала; неизвестный id виден кодом. */
function изСписка(f: LayoutField, v: unknown): string {
  const код = String(v)
  return (f.enum ?? []).find((e) => e.id === код)?.value ?? код
}

/**
 * Число как в Bitrix. Деньги Bitrix присылает строкой вида «515000|RUB» — разбираем
 * на сумму и валюту, а не показываем с палкой; обычный double — по-русски с пробелами.
 */
function число(v: unknown): string {
  const s = String(v)
  if (s.includes('|')) {
    const [сумма, валюта] = s.split('|')
    return fmtMoney(сумма, валюта || 'RUB')
  }
  const n = Number(s)
  return Number.isNaN(n) ? s : n.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
}

/**
 * Привязка crm-поля. Форма Bitrix: голый id (тип сущности в значении не записан — куда
 * ведёт, знает только настройка поля) либо с приставкой «D_112191» / «CO_9063» / «C_16555».
 * С приставкой рисуем ссылку на нашу карточку; голый id показываем номером — угадывать
 * сущность по числу значило бы вести человека на чужую карточку с тем же номером.
 */
function привязкаCrm(v: unknown): ReactNode {
  const s = String(v)
  const m = /^([A-Z]+)_(\d+)$/.exec(s)
  if (m?.[1] === 'D') return <EntityLink kind="deal" id={m[2]} />
  if (m?.[1] === 'CO') return <EntityLink kind="company" id={m[2]} />
  if (m?.[1] === 'C') return <EntityLink kind="contact" id={m[2]} />
  return <span className="mono">{s}</span>
}

/** Одно значение поля раскладки — видом по типу из bx_fields. */
function одноЗначение(f: LayoutField, v: unknown,
                      refs: ReturnType<typeof useRefs>): ReactNode {
  switch (f.type) {
    case 'boolean':
      return v === true || v === 1 || v === '1' || v === 'Y' ? 'да' : 'нет'
    case 'date': return fmtDate(String(v))
    case 'datetime': return fmtDate(String(v), true)
    case 'enumeration':
    case 'crm_status': return изСписка(f, v)
    case 'employee':
    case 'user': return refs.userName(String(v))
    case 'double':
    case 'money': return число(v)
    case 'url': return <a href={String(v)} target="_blank" rel="noreferrer">{String(v)}</a>
    case 'crm': return привязкаCrm(v)
    default: return fmtText(v)
  }
}

/**
 * Значение поля раскладки целиком. Множественные — через запятую; файлы — счётом
 * («файлов: 2»): в зеркале от файла остаётся только его id в Bitrix, самого файла
 * и его имени в ядре нет, и ссылка вела бы в никуда.
 */
function видЗначения(f: LayoutField, v: unknown,
                     refs: ReturnType<typeof useRefs>): ReactNode {
  const список = Array.isArray(v) ? v : [v]
  if (f.type === 'file') {
    return список.length === 1 ? 'файл приложен' : `файлов: ${список.length}`
  }
  if (список.length === 1) return одноЗначение(f, список[0], refs)
  return (
    <>
      {список.map((эл, i) => (
        <Fragment key={i}>
          {i > 0 && ', '}
          {одноЗначение(f, эл, refs)}
        </Fragment>
      ))}
    </>
  )
}

/**
 * Поле раскладки → строка FieldRow. Всё зеркальное — origin='bitrix', read-only с капсулой:
 * правится в Bitrix, а не здесь. Правимых (запись в СВОЮ схему, api/cardLive.ts) ровно два,
 * как и раньше: TITLE и OPPORTUNITY (внутри «Сумма и валюта»).
 */
function полеРаскладки(f: LayoutField, deal: Deal,
                       refs: ReturnType<typeof useRefs>,
                       сотрудники?: cardLive.ВыборСотрудника[]): CardField {
  if (f.code === 'TITLE') {
    return { code: 'TITLE', label: f.title, type: 'text', value: deal.TITLE, origin: 'crm' }
  }
  if (f.code === 'ASSIGNED_BY_ID' && сотрудники?.length) {
    // Ответственный правится в СВОЮ схему (deal.assigned_id, запись у cardLive по
    // вопросу владельца 01.08.2026). Значение выпадашки — номер сотрудника СВОЕЙ
    // схемы; текущего находим по зеркальному Bitrix-номеру. Bitrix-значение остаётся
    // видом строки — историческая справка, в Bitrix не пишется ничего.
    const бх = String(refId(deal.ASSIGNED_BY_ID) ?? '')
    const текущий = сотрудники.find((с) => с.bitrixId === бх)
    return {
      code: 'ASSIGNED_BY_ID', label: f.title, type: 'select',
      value: текущий?.id ?? '', origin: 'crm',
      options: сотрудники.map(({ id, name }) => ({ id, name })),
      display: пустоBitrix(f, deal.ASSIGNED_BY_ID)
        ? ПРОЧЕРК : видЗначения(f, deal.ASSIGNED_BY_ID, refs),
    }
  }
  if (f.code === 'OPPORTUNITY_WITH_CURRENCY') {
    // Псевдополе интерфейса Bitrix: сумма + валюта одной строкой. Правится сумма.
    return {
      code: 'OPPORTUNITY', label: f.title, type: 'number', value: deal.OPPORTUNITY,
      origin: 'crm', display: fmtMoney(deal.OPPORTUNITY, deal.CURRENCY_ID || 'RUB'),
    }
  }
  if (f.code === 'CLIENT') {
    // Псевдополе «Клиент»: компания и контакт сделки. refId чистит «нет связи» ('0').
    const компания = refId(deal.COMPANY_ID)
    const контакт = refId(deal.CONTACT_ID)
    return {
      code: 'CLIENT', label: f.title, type: 'link', value: компания ?? контакт,
      origin: 'bitrix', readOnly: true,
      display: !компания && !контакт ? ПРОЧЕРК : (
        <>
          {/* resolve — одиночное место, догрузить название допустимо */}
          {компания && <EntityLink kind="company" id={компания} resolve />}
          {компания && контакт && ', '}
          {контакт && <EntityLink kind="contact" id={контакт} resolve />}
        </>
      ),
    }
  }
  if (f.code === 'UF_CRM_1502613831') {
    /* Судно — через vesselRefId, а не сырым значением: у 54 сделок в этом поле стоит
       `C_19795` — законная форма привязки Bitrix, из которой получался адрес
       `/vessels/C_19795`, и карточка судна слала этот текст дальше как id.
       Общий refId такую приставку не снимает и не должен (см. lib/smart.ts). */
    const судно = vesselRefId(deal.UF_CRM_1502613831)
    return {
      code: f.code, label: f.title, type: 'link', value: deal.UF_CRM_1502613831,
      origin: 'bitrix', readOnly: true,
      display: refId(судно)
        ? <EntityLink kind="vessel" id={судно} resolve />
        : ПРОЧЕРК,
    }
  }
  const v = deal[f.code]
  return {
    code: f.code, label: f.title, type: 'text', value: v, origin: 'bitrix', readOnly: true,
    display: пустоBitrix(f, v) ? ПРОЧЕРК : видЗначения(f, v, refs),
  }
}

/**
 * Сворачиваемый раздел раскладки — заголовок-кнопка с шевроном, как разделы карточки
 * Bitrix. Строки рисует общий FieldRow; FieldSection здесь не годится ровно одним:
 * его заголовок не кнопка. Свёрнутость живёт в состоянии компонента и не переживает
 * перезагрузку — как и в Bitrix, это поза экрана, а не данные.
 */
function Раздел({ заголовок, поля, onSave }: {
  заголовок: string
  поля: CardField[]
  onSave?: (code: string, value: unknown) => Promise<void>
}) {
  const [открыт, setОткрыт] = useState(true)
  return (
    <section className="fieldsec">
      <button type="button" className="fieldsec-toggle" aria-expanded={открыт}
              onClick={() => setОткрыт((б) => !б)}>
        <span className={`fieldsec-chev${открыт ? ' open' : ''}`} aria-hidden>▸</span>
        {заголовок}
      </button>
      {открыт && поля.map((f) => <FieldRow key={f.code} field={f} onSave={onSave} />)}
    </section>
  )
}

/**
 * Левая колонка полей — 14 разделов ТОЧНО ПО BITRIX: тот же порядок разделов и полей
 * (api/dealLayout.ts, снято из DOM боевой карточки), подписи и типы из зеркала bx_fields.
 *
 * Поля описаны данными (CardField), рисует их общий FieldRow. origin='bitrix' — зеркальное
 * поле: капсула и никакой правки, оно правится в Bitrix. Правимые — TITLE и OPPORTUNITY —
 * помечены origin='crm': их запись идёт в СВОЮ схему (/cards-write/update, api/cardLive.ts),
 * в Bitrix не пишется ничего. Стадия правится полосой стадий, а не строкой поля.
 */
function Поля({ deal, refs, rights, свои, сотрудники, onSave, onSaveExt, onSaveCustom }: {
  deal: NonNullable<ReturnType<typeof useDeal>['data']>
  refs: ReturnType<typeof useRefs>
  rights: ReturnType<typeof useRightsApi>
  /** Поля из конструктора владельца (/api/cards/fields) — undefined, пока грузятся. */
  свои?: cardLive.ОтветПолей
  /** Сотрудники своей схемы для выпадашки «Ответственный» — undefined, пока грузятся
   *  или когда своего id у карточки нет (тогда поле остаётся зеркальным, без правки). */
  сотрудники?: cardLive.ВыборСотрудника[]
  onSave?: (code: string, value: unknown) => Promise<void>
  onSaveExt?: (code: string, value: unknown) => Promise<void>
  onSaveCustom?: (code: string, value: unknown) => Promise<void>
}) {
  // Сколько зеркальных UF-полей вообще приехало. Ручка своей базы (/api/own/deal) сегодня
  // отдаёт только основные колонки и судно — остальные поля раскладки в её ответе
  // ОТСУТСТВУЮТ, и молча показать их прочерками нельзя: прочерк читался бы как «в Bitrix
  // пусто», а правда — «в этом ответе поля нет». Зеркало (/api/bx) отдаёт всё.
  const зеркальныхПриехало = Object.keys(deal).filter((k) => k.startsWith('UF_')).length

  // Поля ИЗ КОНСТРУКТОРА владельца (field_def своей схемы): просмотр и inline-правка
  // по типам — виды совпадают с FieldType строки один в один (text/number/date/select/
  // checkbox/link, cards_fields.ВИДЫ). Архивные сюда не приходят вовсе (ядро отдаёт
  // живые без ?archived=1). Значения — jsonb записи; очищенное ядро не отдаёт.
  const изКонструктора: CardField[] = (свои?.custom ?? []).map((п) => {
    const значение = свои?.values[п.code]
    return {
      code: п.code, label: п.label, type: п.kind, value: значение, origin: 'crm',
      options: п.kind === 'select' ? п.options.map((o) => ({ id: o, name: o })) : undefined,
      href: п.kind === 'link' && typeof значение === 'string' ? значение : undefined,
    }
  })

  // Наши поля из deal_ext — их нет в Bitrix, синк их не трогает. Скалярные правятся
  // (ручка /api/deal/ext существует), объекты показываются как есть.
  const наши: CardField[] = Object.entries(deal._EXT ?? {}).map(([k, v]) => {
    const объект = v !== null && typeof v === 'object'
    return {
      code: k, label: k, type: 'text', value: объект ? JSON.stringify(v) : v,
      origin: 'crm', readOnly: объект,
      readOnlyReason: объект ? 'составное значение — правится не с карточки' : undefined,
    }
  })

  return (
    <div className="card">
      {!rights.canWrite && (
        /* Почему не «серая кнопка с подсказкой»: причина отказа известна
           заранее и словами, а выключенная кнопка сообщает только «нельзя». */
        <div className="hint" style={{ marginBottom: 10 }}>
          Править сделку вашей роли
          {rights.roleTitle ? ` («${rights.roleTitle}»)` : ''} нельзя — раздел
          открыт только на чтение.
          {rights.empty && ' Причина: учётка не связана с сотрудником Bitrix,'
            + ' и проверить, что сделка из вашей зоны, нечем.'}
        </div>
      )}
      {/* «Поля нет в ответе» и «поле в Bitrix пустое» — разные ответы, и склеивать их
          в один прочерк молча нельзя. Порог 1 — судно: единственное UF-поле, которое
          ручка своей базы отдаёт колонкой. */}
      {зеркальныхПриехало <= 1 && (
        <div className="hint" style={{ marginBottom: 10 }}>
          Значения зеркальных полей Bitrix в этом ответе не приехали — ручка своей базы
          отдаёт только основные поля сделки. Прочерк ниже значит «поля нет в ответе»,
          а не «в Bitrix пусто».
        </div>
      )}
      {DEAL_LAYOUT.map((раздел) => (
        <Раздел key={раздел.title} заголовок={раздел.title} onSave={onSave}
                поля={раздел.fields.map((f) => полеРаскладки(f, deal, refs, сотрудники))} />
      ))}
      {/* Секция конструктора видна, когда ядро отдало определения — даже пустые:
          пустота здесь не поломка, а «полей ещё не завели», и сказано, где их заводят. */}
      {свои?.подключено && (
        <FieldSection title="Наши поля" fields={изКонструктора} onSave={onSaveCustom}>
          {изКонструктора.length === 0 && (
            <div className="hint" style={{ marginTop: 6 }}>
              Своих полей у сделок пока нет — поля добавляет владелец
              в «Учётки → Поля карточек».
            </div>
          )}
        </FieldSection>
      )}
      {наши.length > 0 && (
        <FieldSection title="Наши поля (deal_ext)" fields={наши} onSave={onSaveExt} />
      )}
      {rights.canWrite && (
        <div className="hint" style={{ marginTop: 10 }}>
          Запись идёт в свою схему CRM (с проверкой ревизии и журналом изменений) —
          в Bitrix с карточки не пишется ничего.
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════ вкладка «Товары и услуги» ═══════════════════════════

/**
 * Состав сделки (товарные строки Bitrix).
 *
 * Раньше в карточке была видна сумма, но не было видно, ИЗ ЧЕГО она сложилась.
 * А в Bitrix это две независимые вещи: OPPORTUNITY — обычное поле, его правят
 * руками, а состав живёт отдельной таблицей. Поэтому главное здесь не список
 * позиций, а СВЕРКА: у части сделок с составом итог по строкам расходится с
 * суммой самой сделки. Такого экрана нет и в самом Bitrix. Сколько таких сделок
 * сейчас — считает раздел «Расхождения состава»; здесь чисел нет нарочно, они
 * меняются с каждым прогоном синка.
 *
 * Но само число «расходится» бесполезно, пока не сказано, ПОЧЕМУ: подавляющее
 * большинство таких сделок — не ошибка, а способ вести учёт (сумму старых сделок
 * не заводили; работы и доставку в состав не вписывают; торг в пределах 5 %).
 * Поэтому здесь показывается причина от ядра, и красным — только те 702
 * сделки, где данные действительно заведены неверно. Разбор — в шапке
 * products_api.py и в lib/products.ts.
 */
function Состав({ состав, расх }: {
  состав: ReturnType<typeof useСоставСделки>
  расх: ReturnType<typeof разборРасхождения>
}) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Состав</h2>
        <div className="spacer" />
        {состав.data && (
          <span className={`badge ${расх.вид_бейджа}`}>
            {короткоОРасхождении(расх, состав.data.deal.currency)}
          </span>
        )}
      </div>

      <QueryState isLoading={состав.isLoading} error={состав.error}>
        {состав.data && (
          <>
            {/* Сделку не опрашивали — «позиций нет» сказать НЕЛЬЗЯ, мы не знаем.
                Это не педантизм: пустая таблица здесь читалась бы как «сделка без
                состава», то есть как утверждение о содержимом сделки. */}
            {!состав.data.scanned ? (
              <div className="notice">
                Состав этой сделки в ядро ещё не заливался, поэтому показать его нечем.
                Это <b>не</b> значит, что позиций нет — значит, что мы у Bitrix про них
                не спрашивали. Синк опрашивает сделки по мере их изменения
                (<span className="mono">sync_products.py</span>).
              </div>
            ) : (
              <>
                {/* Расхождение — выше таблицы. Оно и есть польза этого блока, и
                    человек должен увидеть его до того, как начнёт вчитываться в строки. */}
                {расх.вид !== 'нет' && (
                  <div className={расх.вид_блока} style={{ marginBottom: 12 }}>
                    <b>{расх.заголовок}</b> {расх.подпись}
                    {/* Причина отдельной строкой, а не в общем абзаце: именно она
                        решает, что человеку делать, и терять её в тексте нельзя. */}
                    {расх.причина && (
                      <div style={{ marginTop: 6 }}>
                        Похоже на: {расх.причина}.
                      </div>
                    )}
                    <div className="hint" style={{ marginTop: 6 }}>
                      {чтоДелать(расх)}
                    </div>
                  </div>
                )}

                {/* Данные могли устареть: сделку тронули уже после того, как мы
                    спросили про её состав. Молчать об этом нельзя — расхождение
                    тогда может быть нашим, а не настоящим. */}
                {состав.data.scan_outdated && (
                  <div className="notice" style={{ marginBottom: 12 }}>
                    Сделку изменили после того, как ядро в последний раз спрашивало про
                    её состав ({fmtDate(состав.data.checked_at, true)}). Состав мог
                    измениться — расхождение ниже стоит перечитать после следующего синка.
                  </div>
                )}

                {состав.data.rows.length === 0 ? (
                  <div className="muted">
                    Товарных строк у сделки нет — Bitrix спрашивали
                    {' '}{fmtDate(состав.data.checked_at, true)}, состав пуст.
                  </div>
                ) : (
                  <>
                    {/* `cards` — на телефоне таблица разбирается на карточки
                        (styles.css, §12). Строка сверки в tfoot набрана заголовками,
                        и правила карточек берут их наравне с ячейками: без этого
                        «Сумма сделки в Bitrix» уезжала бы вбок отдельной таблицей. */}
                    <div className="table-wrap cards">
                      <table>
                        <thead>
                          <tr>
                            <th>Номенклатура</th>
                            <th className="num">Количество</th>
                            <th className="num">Цена</th>
                            <th className="num">Сумма</th>
                            <th>НДС</th>
                            <th>Скидка</th>
                          </tr>
                        </thead>
                        <tbody>
                          {состав.data.rows.map((r) => {
                            const ном = номенклатура(r)
                            return (
                              <tr key={r.bitrix_id}>
                                <td data-label="Номенклатура">
                                  {ном.name}
                                  <div className="hint">
                                    {типСтроки(r.row_type)}
                                    {/* Откуда позиция. У большинства строк каталожной
                                        привязки нет — их вписывают руками, и это
                                        стоит видеть: цена такой строки ниоткуда
                                        не проверяется. */}
                                    {ном.каталог
                                      ? ` · из каталога №${r.product_id}`
                                      : ' · вписана руками'}
                                  </div>
                                </td>
                                <td className="num" data-label="Количество">{количество(r)}</td>
                                <td className="num" data-label="Цена">
                                  {fmtMoney(r.price, состав.data!.deal.currency)}
                                </td>
                                <td className="num" data-label="Сумма">
                                  {fmtMoney(r.amount, состав.data!.deal.currency)}
                                </td>
                                <td data-label="НДС">{ндс(r)}</td>
                                <td data-label="Скидка">{скидка(r) ?? '—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            {/* Название строки итога — само себе подпись. */}
                            <th data-label="">Итог по строкам</th>
                            <th className="num" />
                            <th className="num" />
                            <th className="num" data-label="Сумма">
                              {fmtMoney(состав.data.totals.sum, состав.data.deal.currency)}
                            </th>
                            <th />
                            <th />
                          </tr>
                          {/* Сумма сделки рядом с итогом по строкам, а не «где-то
                              в блоке полей»: сравнить две цифры можно только
                              когда они стоят одна под другой. */}
                          <tr>
                            <th data-label="">Сумма сделки в Bitrix</th>
                            <th className="num" />
                            <th className="num" />
                            <th className="num" data-label="Сумма">
                              {fmtMoney(состав.data.deal.opportunity,
                                        состав.data.deal.currency)}
                            </th>
                            <th />
                            <th />
                          </tr>
                          {расх.вид !== 'нет' && (
                            <tr>
                              {/* Красным — только настоящая ошибка: у подавляющего
                                  большинства сделок расхождение законно, и красная строка
                                  итога там врёт человеку про его же работу. */}
                              <th className={расх.степень === 'error' ? 'error' : ''}
                                  data-label="">
                                Расхождение
                              </th>
                              <th className="num" />
                              <th className="num" />
                              <th className={`num${расх.степень === 'error' ? ' error' : ''}`}
                                  data-label="Сумма">
                                {расх.delta > 0 ? '+' : '−'}
                                {fmtMoney(Math.abs(расх.delta),
                                          состав.data.deal.currency)}
                              </th>
                              <th />
                              <th />
                            </tr>
                          )}
                        </tfoot>
                      </table>
                    </div>

                    <div className="hint" style={{ marginTop: 10 }}>
                      Позиций: {состав.data.totals.lines}
                      {состав.data.totals.from_catalog > 0
                        && `, из каталога Bitrix ${состав.data.totals.from_catalog}`}
                      . Сумма строки — цена за единицу, умноженная на количество, так же
                      считает карточка Bitrix; отдельного поля суммы у строки нет.
                      {/* Строка без цены или без количества не входит в итог. Молчать
                          об этом нельзя: иначе расхождение окажется нашим собственным. */}
                      {состав.data.totals.lines_without_sum > 0 && (
                        <span className="error">
                          {' '}Строк без цены или количества:
                          {' '}{состав.data.totals.lines_without_sum} — в итог они
                          не вошли, поэтому итог по строкам заведомо неполон.
                        </span>
                      )}
                      {состав.data.truncated
                        && ' Состав обрезан пределом ядра — итог по нему неполон.'}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Чего в данных НЕТ. Пустая графа читается как «значение пустое», а не
                «такого поля не существует», поэтому про 1С говорим прямо: связь
                товарной строки с номенклатурой 1С в Bitrix не заполняют. */}
            <div className="hint" style={{ marginTop: 8 }}>
              Привязки к номенклатуре 1С у товарной строки в Bitrix нет: внешний код
              заполнен у считаных строк на всё зеркало, поэтому сопоставить состав с 1С
              по этим данным нельзя. У товарной строки нет и своей отметки времени — свежесть
              состава известна только по времени опроса сделки.
            </div>
          </>
        )}
      </QueryState>
    </div>
  )
}

// ═══════════════════════════ вкладка «Счета» ═══════════════════════════

/**
 * Дебиторка по сделке. В карточке компании эта вкладка есть с самого начала, а в карточке
 * сделки её не было, хотя вопрос «сколько нам ещё должны по ЭТОЙ сделке» задают чаще:
 * компания — это сводка, а платят по сделкам.
 */
function Дебиторка({ pay, payRows, payTotals }: {
  pay: ReturnType<typeof useDealPayments>
  payRows: ReturnType<typeof paymentView>[]
  payTotals: ReturnType<typeof summarize>
}) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Дебиторка по сделке</h2>
        <div className="spacer" />
        {pay.data && !pay.data.stale && payRows.length > 0 && (
          <span className="muted">
            долг {fmtMoney(payTotals.total.sum)}
            {payTotals.buckets.overdue.count > 0 && (
              <span className="error">
                {' '}· просрочено {fmtMoney(payTotals.buckets.overdue.sum)}
              </span>
            )}
          </span>
        )}
      </div>

      <QueryState isLoading={pay.isLoading} error={pay.error}
                  stale={pay.data?.stale} ageMin={pay.data?.ageMin}
                  scope={pay.data?.scope}>
        {/* Компактный вид: колонки «Компания» и «Сделка» на этом экране были бы одним
            и тем же значением во всех строках. */}
        <PaymentsTable rows={payRows} compact stale={pay.data?.stale}
                       emptyText="Платежей по этой сделке в дебиторке нет" />

        {/* На устаревшей выборке сумму долга не печатаем и здесь — ровно по той же
            причине, по которой её не печатает счётчик в заголовке: цифра, сложенная
            из отставших строк, читается как долг сделки, а она им не является. */}
        {payRows.length > 0 && !pay.data?.stale && (
          <div className="hint" style={{ marginTop: 10 }}>
            Платежей у сделки: {payRows.length}
            {payTotals.paid.count > 0
              && `, из них оплачено ${payTotals.paid.count} на ${fmtMoney(payTotals.paid.sum)}`}
            {payTotals.failed.count > 0
              && `, списано ${payTotals.failed.count} на ${fmtMoney(payTotals.failed.sum)}`}
            . Долг {fmtMoney(payTotals.total.sum)} — по {payTotals.total.count}
            {' '}{plural(payTotals.total.count, 'неоплаченному платежу',
                         'неоплаченным платежам', 'неоплаченным платежам')};
            {' '}считается как «сумма платежа минус поступление», тем же кодом, что
            в разделе «Дебиторка» и в карточке компании.
            {payTotals.dirty > 0
              && ` С противоречивыми признаками оплаты: ${payTotals.dirty} — помечены «?».`}
            {(pay.data?.total ?? 0) > payRows.length
              && ` Показана первая страница: ${payRows.length} из ${pay.data?.total},`
               + ' суммы — по ней же (ядро отдаёт по'
               + ` ${pluralN(PAGE, 'записи', 'записи', 'записей')} и агрегатов не умеет).`}
          </div>
        )}

        {/* Полнота отбора. Молчать об этом нельзя: «платежей нет» и «платежи есть,
            но привязаны иначе» — разные ответы, и второй меняет решение. */}
        <div className="hint" style={{ marginTop: 8 }}>
          Платежи отобраны по родителю смарт-процесса 134 (`parentId2`). У платежа есть
          и второе поле-привязка к сделке; на 26.07.2026 из 16 216 живых платежей только
          у одного заполнено оно, а родитель — нет, и такой платёж здесь не покажется.
          Ядро умеет в фильтре только «И», поэтому «одно или другое» одним запросом
          не выразить.
        </div>
      </QueryState>
    </div>
  )
}

// ═══════════════════════════ где сейчас сделка ═══════════════════════════

/**
 * Отказ ядра по-русски. Транспорт (api/chain.ts) складывает сообщение из `error`, и у отказа
 * в правах там стоит машинный код `forbidden` — человеку он не говорит ничего, а на карточке
 * читается как поломка. Переводим только те коды, у которых смысл однозначен; всё остальное
 * показываем как пришло: свои формулировки поверх чужих ошибок скрывают настоящую причину.
 */
function причинаОтказа(e: unknown): string {
  const т = (e as Error)?.message ?? ''
  if (т === 'forbidden' || т === 'no_rights') {
    return 'сквозной путь сделки вашей роли не показывают'
  }
  if (т === 'core_unreachable') return 'ядро не ответило'
  return т || 'причину ядро не назвало'
}

/**
 * Ответ в одну строку: встало / прошла / судить нечем. Считает его ядро (`stuck_at`,
 * `gaps`), здесь только показ — тем же `итогПути`, что и на экране «Путь сделки».
 *
 * Отказ ядра здесь ОБЪЯСНЯЕТСЯ словами, а не проглатывается. Молча спрятать блок значило бы
 * сказать «разрывов нет»: карточка без вывода читается как карточка без проблем.
 */
function Вывод({ путь, п, итог }: {
  путь: ReturnType<typeof useПутьСделки>
  п?: ПутьСделки
  итог: ReturnType<typeof итогПути>
}) {
  if (путь.isLoading) return null
  if (путь.error) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="muted">
          Сквозной путь этой сделки ядро не отдало: {причинаОтказа(путь.error)}. Это
          <b> не</b> значит, что этапы не пройдены, — значит, что спросить про них сейчас
          нечем. Дебиторка и состав ниже приходят другими ручками и от этого не зависят.
        </div>
      </div>
    )
  }
  if (!п) return null
  const прочие = п.gaps.filter((g) => g.code !== п.stuck_at?.code)
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <span className={итог.класс}>{итог.заголовок}</span>
        <span className="muted">{итог.пояснение}</span>
      </div>
      {прочие.length > 0 && (
        <ul className="plain-list" style={{ marginTop: 12 }}>
          {прочие.map((g: Разрыв) => (
            <li key={g.code}>
              <span className={классВажности(g.severity)}>{g.severity}</span>{' '}
              <b>{g.title}</b> <span className="muted">— {g.why}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ═══════════════════════════ вкладка «Путь сделки» ═══════════════════════════

/**
 * Этапы по порядку цепочки, а не по дате: чинить надо самое раннее место. Порядок задаёт
 * ядро, и он же — порядок поиска «где встало».
 *
 * Здесь и живут те разделы, ради которых раньше приходилось уходить из карточки: договоры
 * (153), счета клиенту (31), счета на оплату поставщику (1160), АВР (170), отчёты по
 * сделке (176), верификации и отчёты инженеров портала 73.
 */
function Цепочка({ п, refs }: { п: ПутьСделки; refs: ReturnType<typeof useRefs> }) {
  const [раскрыто, setРаскрыто] = useState<Record<string, boolean>>({})
  const разрывПо = useMemo(() => {
    const m: Record<string, Разрыв[]> = {}
    for (const g of п.gaps) (m[g.step] ||= []).push(g)
    return m
  }, [п.gaps])

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Цепочка</h2>
      <div className="muted" style={{ marginBottom: 12 }}>
        Порядок — как в цепочке, а не по дате: рядом видно, когда этапы шли не по порядку.
        «Не знаем» — это не пропуск, а граница данных: под строкой написано, почему.
        Числом в колонке «Записей» раскрываются сами записи этапа.
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Этап</th>
              <th>Состояние</th>
              <th className="num">Записей</th>
              <th>Когда</th>
              <th>Что именно</th>
            </tr>
          </thead>
          <tbody>
            {п.steps.map((з: Звено) => {
              const с = состояние(з.state)
              const свои = разрывПо[з.key] ?? []
              const встало = п.stuck_at?.step === з.key
              const открыто = !!раскрыто[з.key]
              return (
                <Fragment key={з.key}>
                  <tr>
                    <td data-label="Этап">
                      <b>{з.title}</b>
                      {встало && <> <span className="badge lose">здесь встало</span></>}
                      <div className="hint">{з.source}</div>
                    </td>
                    <td className="nowrap" data-label="Состояние">
                      <span className={с.класс} title={с.смысл}>{с.ярлык}</span>
                    </td>
                    <td className="num" data-label="Записей">
                      {з.count > 0 ? (
                        <button type="button"
                                onClick={() => setРаскрыто({ ...раскрыто, [з.key]: !открыто })}>
                          {з.count}
                        </button>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td className="nowrap mono" data-label="Когда">
                      {з.at ?? <span className="muted">—</span>}
                    </td>
                    <td data-label="Что именно">
                      {з.items.length > 0
                        ? сутьЗаписи(з.key, з.items[0]) || <span className="muted">без подробностей</span>
                        : <span className="muted">—</span>}
                      {з.count > 1 && <span className="muted"> и ещё {з.count - 1}</span>}
                    </td>
                  </tr>

                  {/* Почему звено пустое — словами ядра. Это и есть разница между честным
                      экраном и пустой клеткой. */}
                  {(з.note || з.state === 'не_знаем' || з.state === 'не_сведено') && (
                    <tr>
                      <td colSpan={5} className="hint">
                        {з.note || с.смысл}
                        {з.since && з.state === 'не_знаем' && (
                          <> Этот учёт ведут с {з.since}
                            {п.deal.day ? `, а сделка датирована ${п.deal.day}` : ''}.</>
                        )}
                      </td>
                    </tr>
                  )}

                  {свои.map((g) => (
                    <tr key={`${з.key}-${g.code}`}>
                      <td colSpan={5}>
                        <span className={классВажности(g.severity)}>{g.severity}</span>{' '}
                        <b>{g.title}</b> <span className="muted">— {g.why}</span>
                      </td>
                    </tr>
                  ))}

                  {открыто && з.items.map((r: ЗаписьЗвена, i: number) => (
                    <tr key={`${з.key}-item-${r.id ?? r.payment_id ?? i}`}>
                      <td className="muted" data-label="">{i + 1}.</td>
                      <td className="nowrap" data-label="Состояние">
                        {r.stage_id
                          ? (
                            <span className={`badge ${refs.itemStageKind(r.stage_id)}`}>
                              {refs.itemStageName(r.stage_id)}
                            </span>
                          )
                          : <span className="muted">—</span>}
                      </td>
                      <td className="num mono" data-label="Записей">{r.id ?? r.payment_id ?? ''}</td>
                      <td className="nowrap mono" data-label="Когда">{r.at ?? '—'}</td>
                      <td data-label="Что именно">
                        {сутьЗаписи(з.key, r) || fmtText(r.title)}
                        {/* Запись нашлась по своему полю-привязке, а не по родителю. У семи
                            платежей две привязки указывают на РАЗНЫЕ сделки, и такая запись
                            появится в пути обеих: молчать об этом нельзя. */}
                        {r.by_parent === false && (
                          <> <span className="badge" title="Найдено по полю-привязке записи, а не по родителю (parentId2). У части платежей эти две привязки указывают на разные сделки.">
                            по полю-привязке
                          </span></>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ═══════════════════════════ вкладка «Документы» ═══════════════════════════

/**
 * Документы 1С этой сделки. Заказы лежат отдельно от реализаций намеренно: «заказ есть,
 * отгрузки нет» — это утверждение, и заказ здесь его единственное доказательство.
 *
 * Блок не прячется, когда документов нет: у зеркала документов есть день начала, и до этого
 * дня пустота не означает ничего. Сказать это надо словами — иначе «в 1С ничего нет» станет
 * выводом о работе людей, а не о границах зеркала.
 */
function Документы1С({ п }: { п: ПутьСделки }) {
  const d = п.docs_1c
  const группы: Array<{ ключ: string; имя: string; строки: ЗаписьЗвена[]; смысл: string }> = [
    { ключ: 'orders', имя: 'Заказы клиента', строки: d.orders,
      смысл: 'что клиенту продали по документам 1С' },
    { ключ: 'sales', имя: 'Реализации', строки: d.sales,
      смысл: 'что списано со склада: это и есть отгрузка по учёту, а не отметка в Bitrix' },
    // Оговорку про связь берём словами ядра, а не своими: у заказа поставщику своего поля
    // связи со сделкой в 1С практически нет, номер разбирают из комментария. Написать здесь
    // «чем закрывали потребность сделки» и промолчать об этом значило бы выдать догадку
    // разборщика за учётную связь.
    { ключ: 'supply', имя: 'Поступления и заказы поставщику', строки: d.supply,
      смысл: d.supply_link || 'чем закрывали потребность сделки' },
  ]
  const всего = группы.reduce((n, г) => n + г.строки.length, 0)
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Документы 1С</h2>
      {всего === 0 ? (
        <div className="muted">
          Документов 1С по этой сделке в зеркале нет.
          {d.since
            ? ` Зеркало документов ведут с ${d.since}: если сделка старше, отсутствие`
              + ' документов не говорит ничего — их просто некуда было записать.'
            : ' С какого дня ведут зеркало документов, ядро не сообщило, поэтому судить'
              + ' о том, значит ли эта пустота что-нибудь, нельзя.'}
          {' '}Связь идёт по номеру сделки Bitrix в справочнике сделок 1С: сделка, которой
          в 1С не заводили, сюда не попадёт вовсе.
        </div>
      ) : (
        группы.filter((г) => г.строки.length > 0).map((г) => (
          <div key={г.ключ} style={{ marginBottom: 12 }}>
            <div><b>{г.имя}</b> <span className="muted">— {г.смысл}</span></div>
            <ul className="plain-list" style={{ marginTop: 6 }}>
              {г.строки.map((r, i) => (
                <li key={r.ref ?? `${г.ключ}-${i}`}>
                  <span className="mono">{сутьЗаписи('realization_1c', r)}</span>
                  {r.at && <span className="muted"> · {String(r.at).slice(0, 10)}</span>}
                  {/* Состояние документа приходит из 1С как есть («Закрыт», «КОбеспечению»).
                      Своего словаря не сочиняем: перевод чужих статусов — это домысел,
                      а кладовщик ищет документ у себя ровно по этому слову. */}
                  {r.status && <span className="muted"> · {r.status}</span>}
                  {/* У заказа плановая дата — это обещание клиенту, и без неё «заказ есть,
                      отгрузки нет» не отличить от «ещё не срок». */}
                  {r.plan_date && (
                    <span className="muted"> · план {String(r.plan_date).slice(0, 10)}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  )
}

// ═══════════════════════════ рядом по судну ═══════════════════════════

/**
 * То, что к сделке НЕ привязано, — отдельным блоком, и это принципиально. Акты на
 * оборудование не имеют номера сделки ни в одной из 2 630 записей зеркала (замер 28.07.2026):
 * единственная связь — IMO судна. Поставь их в цепочку, и «акт есть» прочиталось бы как
 * «акт по этой сделке», хотя
 * у судна бывает десяток сделок в год.
 */
function Рядом({ п }: { п: ПутьСделки }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2>Рядом, но не по сделке</h2>
      <div className="muted" style={{ marginBottom: 12 }}>
        Это НЕ этапы цепочки: к сделке они не привязаны. Показаны, чтобы было куда смотреть
        дальше, — но выводов о ходе этой сделки из них делать нельзя.
      </div>
      {п.nearby.map((н: Звено) => {
        const с = состояние(н.state)
        return (
          <div key={н.key} style={{ marginBottom: 12 }}>
            <div>
              <b>{н.title}</b> <span className={с.класс}>{с.ярлык}</span>
              {typeof н.total === 'number' && н.total > н.count && (
                <span className="muted"> — показано {н.count} из {н.total}</span>
              )}
            </div>
            <div className="hint">
              Связь: {н.link}. {н.why ?? ''} {н.note ?? ''}
            </div>
            {/* Судно заведено больше одного раза — акты могли прийти от другой карточки
                того же IMO. 361 номер IMO в зеркале имеет по нескольку записей. */}
            {н.key === 'equip_acts' && п.vessel && п.vessel.imo_rows > 1 && (
              <div className="notice" style={{ marginTop: 8, marginBottom: 0 }}>
                Судно с IMO {п.vessel.imo} заведено в CRM{' '}
                {pluralN(п.vessel.imo_rows, 'раз', 'раза', 'раз')}. Акты
                найдены по номеру IMO, поэтому часть из них может относиться к другой карточке
                того же судна.
              </div>
            )}
            {н.items.length > 0 && (
              <ul className="plain-list" style={{ marginTop: 8 }}>
                {н.items.map((r: ЗаписьЗвена, i: number) => (
                  <li key={r.id ?? i}>
                    <span className="mono">{сутьЗаписи(н.key, r)}</span>
                    {r.vessel_name && <span className="muted"> · {r.vessel_name}</span>}
                    {r.status && <span className="muted"> · {r.status}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
      {п.p73_requests.length > 0 && (
        <div>
          <b>Заявки портала 73</b>
          <ul className="plain-list" style={{ marginTop: 8 }}>
            {п.p73_requests.map((з) => (
              <li key={з.id}>
                <span className="mono">#{з.id}</span> {fmtText(з.title)}
                <span className="muted">
                  {' '}· {fmtText(з.status)}{з.location ? ` · ${з.location}` : ''}
                  {' '}· создана {fmtDate(з.created)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════ переносы срока оплаты ═══════════════════════════

/**
 * Журнал переносов «примерной даты оплаты». В Bitrix остаётся ТОЛЬКО последнее значение —
 * истории там нет, и без этого журнала перенос задним числом невидим: долг выглядит свежим.
 * Пустой журнал НЕ означает «не переносили»: его ведут с 08.07.2026, и блок в этом случае
 * не показывается вовсе — утверждать по нему нечего.
 */
function Переносы({ п }: { п: ПутьСделки }) {
  const задним = переносыЗадним(п)
  return (
    <div className="card">
      <h2>Срок оплаты переносили</h2>
      <div className="muted" style={{ marginBottom: 12 }}>
        {п.due_moves.length} {плюрал(п.due_moves.length, 'перенос', 'переноса', 'переносов')}
        {задним.length > 0 && (
          <>
            , из них {задним.length} — <b>после того, как срок уже наступил</b>. Такой перенос
            прячет просрочку: в Bitrix виден только последний срок
          </>
        )}
        . Историю знает только журнал ядра, и вести его начали 08.07.2026: отсутствие старых
        переносов означает «не наблюдали», а не «не было».
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Платёж</th>
              <th>Было</th>
              <th>Стало</th>
              <th>Когда перенесли</th>
              <th>Задним числом</th>
            </tr>
          </thead>
          <tbody>
            {п.due_moves.map((m, i) => (
              <tr key={`${m.payment_id}-${m.changed_at ?? i}`}>
                <td className="mono" data-label="Платёж">#{m.payment_id}</td>
                <td className="nowrap mono" data-label="Было">{fmtText(m.old)}</td>
                <td className="nowrap mono" data-label="Стало">{fmtText(m.new)}</td>
                <td className="nowrap" data-label="Когда перенесли">{fmtDate(m.changed_at, true)}</td>
                <td data-label="Задним числом">
                  {m.after_due
                    ? <span className="badge lose">да</span>
                    : <span className="muted">нет</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
